/**
 * BerichtenPage — één communicatielijn met kantoor.
 *
 * Toont berichten van kantoor (die je kunt afwerken) én je eigen verzonden
 * berichten. Tik een bericht open → thread met reageren + afhandelen.
 * "Nieuw bericht" stuurt een (algemeen) bericht naar kantoor.
 */

import { useCallback, useEffect, useState } from 'react'
import { useBerichtenStore } from '../stores'
import {
  fetchBerichtList,
  type BerichtSummary,
  type Richting,
} from '../lib/berichten'
import { BerichtComposer } from '../components/berichten/BerichtComposer'
import { BerichtConversation } from '../components/berichten/BerichtThread'

type RichtingFilter = 'office_to_field' | 'field_to_office' | 'all'

export function BerichtenPage() {
  const refreshBadge = useBerichtenStore((s) => s.refresh)

  const [berichten, setBerichten] = useState<BerichtSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [richting, setRichting] = useState<RichtingFilter>('office_to_field')
  const [showAfgehandeld, setShowAfgehandeld] = useState(false)
  const [openThreadId, setOpenThreadId] = useState<string | null>(null)
  const [showComposer, setShowComposer] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const rows = await fetchBerichtList({
        richting: richting === 'all' ? undefined : (richting as Richting),
        status: showAfgehandeld ? 'all' : 'open',
      })
      setBerichten(rows)
    } catch (err) {
      console.error('[Berichten] load error:', err)
    } finally {
      setLoading(false)
    }
  }, [richting, showAfgehandeld])

  useEffect(() => {
    load()
  }, [load])

  const tabs: { key: RichtingFilter; label: string }[] = [
    { key: 'office_to_field', label: 'Van kantoor' },
    { key: 'field_to_office', label: 'Verzonden' },
    { key: 'all', label: 'Alles' },
  ]

  return (
    <div className="space-y-lg">
      <section className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-kleda-text-main">Berichten</h2>
          <p className="text-kleda-text-muted text-sm">Communicatie met kantoor</p>
        </div>
        <button
          type="button"
          onClick={() => setShowComposer(true)}
          className="bg-kleda-primary text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          Nieuw bericht
        </button>
      </section>

      {/* Filter */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex border-b border-kleda-border">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRichting(tab.key)}
              className={`px-md py-2 text-sm font-medium border-b-2 transition-colors ${
                richting === tab.key
                  ? 'border-kleda-primary text-kleda-primary'
                  : 'border-transparent text-kleda-text-muted hover:text-kleda-text-main'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowAfgehandeld((v) => !v)}
          className="text-xs text-kleda-text-muted hover:text-kleda-text-main"
        >
          {showAfgehandeld ? 'Verberg afgehandeld' : 'Toon afgehandeld'}
        </button>
      </div>

      {/* List */}
      <section className="space-y-sm">
        {loading ? (
          <p className="text-kleda-text-muted text-sm text-center py-8">Laden…</p>
        ) : berichten.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-kleda-text-muted text-sm">Geen berichten</p>
          </div>
        ) : (
          berichten.map((bericht) => (
            <BerichtCard
              key={bericht.id}
              bericht={bericht}
              isOpen={openThreadId === bericht.id}
              onToggle={() => setOpenThreadId((cur) => (cur === bericht.id ? null : bericht.id))}
              onChanged={() => {
                load()
                refreshBadge()
              }}
            />
          ))
        )}
      </section>

      <BerichtComposer
        isOpen={showComposer}
        onClose={() => setShowComposer(false)}
        onSent={() => {
          load()
          refreshBadge()
        }}
      />
    </div>
  )
}

interface CardProps {
  bericht: BerichtSummary
  isOpen: boolean
  onToggle: () => void
  onChanged: () => void
}

function BerichtCard({ bericht, isOpen, onToggle, onChanged }: CardProps) {
  const fromOffice = bericht.richting === 'office_to_field'
  const isAfgehandeld = bericht.status === 'afgehandeld'
  const accent = fromOffice && !isAfgehandeld ? 'border-amber-300 bg-amber-50/40' : 'border-kleda-border bg-white'

  return (
    <div className={`border rounded-lg ${accent}`}>
      <button type="button" onClick={onToggle} className="w-full text-left px-3 py-3 flex items-start gap-2">
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-kleda-text-main truncate">
              {bericht.onderwerp || bericht.laatsteReactiePreview || '(geen onderwerp)'}
            </span>
            {bericht.prioriteit === 'urgent' && (
              <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700">
                Urgent
              </span>
            )}
            {isAfgehandeld && (
              <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium bg-green-100 text-green-700">
                Afgehandeld
              </span>
            )}
          </div>
          <div className="text-[11px] text-kleda-text-muted flex items-center gap-1 flex-wrap">
            <span>{fromOffice ? 'Van kantoor' : 'Verzonden'}</span>
            {bericht.werkOrderName && <span>· {bericht.werkOrderName}</span>}
            {bericht.wasserijItemName && <span>· {bericht.wasserijItemName}</span>}
            <span>· {bericht.aantalReacties} bericht{bericht.aantalReacties === 1 ? '' : 'en'}</span>
          </div>
        </div>
        <span className="text-kleda-text-muted text-xs mt-1">{isOpen ? '▴' : '▾'}</span>
      </button>
      {isOpen && <BerichtConversation berichtId={bericht.id} onChanged={onChanged} />}
    </div>
  )
}
